import { useState, useCallback, useEffect, type ReactNode } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import { useTheme } from '@mui/material/styles';
import { usePathname } from 'next/navigation';

import { NavList } from './nav-list';
import { navSectionClasses } from '../classes';
import { navSectionCssVars } from '../css-vars';
import { NavUl, NavLi } from '../styles';
import { pathForNextNavigation } from '@/lib/appBasePath';
import { Iconify } from '../../iconify';

import type { NavGroupProps, NavSectionProps, NavListProps } from '../types';

// ----------------------------------------------------------------------
// Command Center .ngroup-t — colored dot, uppercase micro label, chevron.

function NavGroupToggle({
  children,
  dotColor,
  collapsed,
  onClick,
}: {
  children: ReactNode;
  dotColor?: string;
  collapsed?: boolean;
  onClick?: () => void;
}) {
  return (
    <Box
      component={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--text-micro)',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--text-primary)',
        lineHeight: 1.5,
        px: '10px',
        py: '4px',
        borderRadius: '8px',
        textAlign: 'left',
        cursor: onClick ? 'pointer' : 'default',
        bgcolor: 'transparent',
        border: 'none',
        userSelect: 'none',
        '&:hover': {
          bgcolor: 'var(--bg-hover)',
        },
      }}
    >
      {dotColor ? (
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
      ) : null}
      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
        {children}
      </Box>
      <Box component="span" sx={{ ml: 'auto', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', flexShrink: 0, opacity: 0.6 }}>
        <Iconify icon={collapsed ? 'eva:arrow-ios-forward-fill' : 'eva:arrow-ios-downward-fill'} width={12} />
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------

export function NavSectionVertical({
  sx,
  data,
  render,
  slotProps,
  enabledRootRedirect,
  cssVars: overridesVars,
}: NavSectionProps) {
  const theme = useTheme();

  const cssVars = {
    ...navSectionCssVars.vertical(theme),
    ...overridesVars,
  };

  // Only set white background for Alcian theme and light mode
  const alcianLightBg = (theme.palette?.mode === 'light' && theme.palette?.primary?.main === '#00A76F') ? { backgroundColor: '#fff' } : {};
  return (
    <Stack
      component="nav"
      className={navSectionClasses.vertical.root}
      sx={{
        ...alcianLightBg,
        ...cssVars,
        ...sx,
        fontFamily: 'var(--font-body)',
        gap: '2px',
      }}
    >
      <NavUl sx={{ flex: '1 1 auto', gap: '2px' }}>
        {data.map((group) => (
          <Group
            key={typeof group.subheader === 'string' ? group.subheader : group.subheader?.title ?? group.items[0].title}
            subheader={group.subheader}
            items={group.items}
            render={render}
            slotProps={slotProps}
            enabledRootRedirect={enabledRootRedirect}
          />
        ))}
      </NavUl>
    </Stack>
  );
}

// ----------------------------------------------------------------------

function Group({ items, render, subheader, slotProps, enabledRootRedirect }: NavGroupProps) {
  const pathname = usePathname();

  const checkIsActive = useCallback(
    (lists: NavListProps['data'][]): boolean => {
      const normPath = pathForNextNavigation(pathname || '');

      return lists.some((list) => {
        const itemPath = pathForNextNavigation(list.path || '');

        if (itemPath && itemPath !== '/' && itemPath !== '#' && !itemPath.startsWith('http')) {
          const isMatch = list.children ? normPath.includes(itemPath) : normPath === itemPath;
          if (isMatch) return true;
        }

        if (list.children) {
          return checkIsActive(list.children);
        }

        return false;
      });
    },
    [pathname]
  );

  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (label: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  useEffect(() => {
    const groups: Array<{ label: string; itemTitles: string[] }> =
      (subheader && typeof subheader === 'object' && 'groups' in subheader)
        ? (subheader as any).groups ?? []
        : [];
    setCollapsedGroups((prev) => {
      const next = { ...prev };
      groups.forEach((group) => {
        const groupItems = items.filter((item) => group.itemTitles.includes(item.title));
        if (checkIsActive(groupItems)) {
          next[group.label] = false;
        }
      });
      return next;
    });
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const isFamily =
    subheader &&
    typeof subheader === 'object' &&
    'family' in subheader &&
    (subheader as any).family === true;

  const isHidden =
    subheader &&
    typeof subheader === 'object' &&
    'hidden' in subheader &&
    (subheader as any).hidden === true;

  const familyGroups: Array<{ label: string; itemTitles: string[] }> =
    (subheader && typeof subheader === 'object' && 'groups' in subheader)
      ? (subheader as any).groups ?? []
      : [];

  // For family-style nav sections the content is always visible (not collapsible at family level)
  const renderFamilyContent = () => {
    if (familyGroups.length === 0) {
      // No sub-groups — render items directly
      return (
        <NavUl sx={{ gap: 0 }}>
          {items.map((list) => (
            <NavList
              key={list.title}
              data={list}
              render={render}
              depth={1}
              slotProps={slotProps}
              enabledRootRedirect={enabledRootRedirect}
            />
          ))}
        </NavUl>
      );
    }

    // With sub-groups: render labeled group headers + their items
    return (
      <NavUl sx={{ gap: 0 }}>
        {familyGroups.map((group) => {
          const groupItems = items.filter((item) => group.itemTitles.includes(item.title));
          const isCollapsed = !!collapsedGroups[group.label];
          return (
            <NavLi key={group.label}>
              {/* Group header label — click to collapse/expand */}
              <NavGroupToggle
                onClick={() => toggleGroup(group.label)}
                collapsed={isCollapsed}
              >
                {group.label}
              </NavGroupToggle>
              {/* Group items — collapsible */}
              <Collapse in={!isCollapsed} unmountOnExit>
                <NavUl sx={{ gap: 0 }}>
                  {groupItems.map((list) => (
                    <NavList
                      key={list.title}
                      data={list}
                      render={render}
                      depth={1}
                      slotProps={slotProps}
                      enabledRootRedirect={enabledRootRedirect}
                    />
                  ))}
                </NavUl>
              </Collapse>
            </NavLi>
          );
        })}
      </NavUl>
    );
  };

  // Support section (hidden header, utility items at bottom)
  if (isHidden) {
    return (
      <NavLi>
        {/* Separator before support area */}
        <Box sx={{ height: 1, bgcolor: 'var(--line)', mx: '10px', my: '7px' }} />
        <NavUl sx={{ gap: 0, pb: 1 }}>
          {items.map((list) => (
            <NavList
              key={list.title}
              data={list}
              render={render}
              depth={1}
              slotProps={{
                ...slotProps,
                rootItem: {
                  ...slotProps?.rootItem,
                  sx: {
                    opacity: 0.55,
                    fontSize: '12px',
                  },
                },
              }}
              enabledRootRedirect={enabledRootRedirect}
            />
          ))}
        </NavUl>
      </NavLi>
    );
  }

  // Family header section — Command Center .ngroup-t
  if (isFamily && subheader && typeof subheader === 'object') {
    const { title, familyColor } = subheader as any;
    return (
      <NavLi sx={{ px: '12px', pt: '10px', pb: '2px' }}>
        <NavGroupToggle dotColor={familyColor} collapsed={false}>
          {title}
        </NavGroupToggle>
        {renderFamilyContent()}
      </NavLi>
    );
  }

  // Fallback: legacy plain string subheader (not used in new navData but kept for safety)
  const renderContent = (
    <NavUl sx={{ gap: 0 }}>
      {items.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={1}
          slotProps={slotProps}
          enabledRootRedirect={enabledRootRedirect}
        />
      ))}
    </NavUl>
  );

  return (
    <NavLi>
      {renderContent}
    </NavLi>
  );
}
